import { IconX } from "@tabler/icons-react";
import { ArrowUp } from "lucide-react";
import { type ComponentProps, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  PromptInput,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputTextarea,
  PromptInputTools,
  type PromptInputMessage,
} from "#app/components/ai-elements/prompt-input";
import { Button } from "#app/components/ui/button";
import { cn } from "#app/utils/misc.tsx";

/**
 * The chat composer: Vercel AI Elements' `PromptInput`, styled after the
 * prototype's `components/chat/chat-input.tsx` — a rounded-3xl card holding the
 * text surface, with a divided action bar underneath (secondary pills on the
 * left, outlined mic and a filled arrow-up submit on the right).
 *
 * `PromptInput` is a real `<form>`: the textarea submits on Enter and newlines
 * on Shift+Enter, and the form owns attachment state and drag-and-drop. It
 * replaced a Quill editor here, so rich-text paste and the markdown toolbar are
 * gone — the prototype's input is a plain textarea and AI Elements' is too.
 */

type ChatComposerProps = {
  /**
   * Composer-scoped notice above the chips — pending sends waiting on the
   * current stream. Composer state, not transcript state, so it lives here
   * rather than in the message list.
   */
  banner?: ReactNode;
  /** Chips for attachments and context files, above the text surface. */
  chips?: ReactNode;
  /** Left-hand actions: add context, tools, tips. */
  leftActions?: ReactNode;
  /** Right-hand actions: clear, enrich, mic, stop, send. */
  rightActions?: ReactNode;
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  onSubmit: (message: PromptInputMessage) => void;
  /** Rendered under the textarea, above the action bar (the dictation preview). */
  afterInput?: ReactNode;
  textareaClassName?: string;
  className?: string;
} & Omit<
  ComponentProps<typeof PromptInput>,
  "onSubmit" | "onChange" | "value" | "className" | "children"
>;

export function ChatComposer({
  banner,
  chips,
  leftActions,
  rightActions,
  placeholder,
  value,
  onValueChange,
  onSubmit,
  afterInput,
  textareaClassName,
  className,
  ...props
}: ChatComposerProps) {
  return (
    <PromptInput
      onSubmit={(message, event) => {
        event.preventDefault();
        onSubmit(message);
      }}
      // PromptInput puts `className` on its <form> and renders an <InputGroup>
      // inside, so the card styling has to reach through to that group —
      // putting it on the form leaves the group's own pill styling in place and
      // collapses the textarea.
      className={cn(
        // `relative z-10`: <ChatLandingBackdrop> is `absolute inset-0 z-0`, and a
        // positioned z-0 element paints above a *non*-positioned sibling no matter
        // the DOM order. Without this the wash and its 48px grid painted over the
        // card and the opaque `bg-card` below read as a transparent composer.
        // The hero and the suggestion row already opt in the same way.
        "lobe-chat-box relative z-10 w-full",
        // `shadow-xl` on every placement, not only the empty-chat hero: the
        // prototype renders one `chat-input.tsx` card and reuses it verbatim
        // under an active thread (`chat-box.tsx` mounts the same `<ChatInput>`
        // in both branches), so a flat docked card read as the composer losing
        // its lift the moment the first message landed.
        "[&>[data-slot=input-group]]:rounded-3xl [&>[data-slot=input-group]]:border-border [&>[data-slot=input-group]]:bg-card dark:[&>[data-slot=input-group]]:bg-[#1F1F20] [&>[data-slot=input-group]]:shadow-xl",
        className,
      )}
      {...props}
    >
      {banner || chips ? (
        <PromptInputHeader className="flex-col items-stretch gap-0 p-0!">
          {banner}
          {chips ? (
            <div className="flex flex-wrap gap-2 p-2 pb-0">{chips}</div>
          ) : null}
        </PromptInputHeader>
      ) : null}

      {/* No <PromptInputBody> wrapper. It renders `display: contents`, and this
          app's InputGroup switches to a column with `has-[>[data-align=block-end]]`
          — a DIRECT-child selector, which `contents` does not satisfy. With the
          wrapper in place the group stayed a 36px-tall row and squashed the
          textarea to a sliver. */}
      <PromptInputTextarea
        data-tour="chat-input"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onValueChange?.(event.target.value)}
        className={cn(
          "min-h-16 resize-none overflow-y-auto bg-transparent px-4 py-3.5 text-base",
          textareaClassName,
        )}
      />

      {/* `border-border/50`, not `border-border`: the prototype divides the
          action bar with `border-zinc-100` (#f4f4f5), while this app's
          `--border` is `240 5.9% 90%` (#e3e3e8) — dark enough that the
          separator read as a hard rule cutting the card in two. Halving the
          token lands on ~#f1f1f3 over `bg-card` and stays theme-aware in dark
          mode, where a literal zinc would invert wrongly. */}
      <PromptInputFooter className="flex-wrap border-t border-border/50 dark:border-zinc-800">
        {afterInput ? <div className="w-full min-w-0">{afterInput}</div> : null}
        {/* `-ml-1` optically aligns the leading icon with the placeholder above
            it. The footer addon is `px-3` (12px) and the paperclip is a 36px
            button around a 20px glyph, so the glyph landed at 20px while the
            textarea's `px-4` puts the text at 16px. Pulling the group 4px left
            matches them without moving the text or the right-hand group, whose
            12px edge inset is correct. Same idiom as InputGroupAddon's own
            `has-[>button]:-ml-1`. */}
        <PromptInputTools className="left-chat-btns -ml-1 gap-2">
          {leftActions}
        </PromptInputTools>
        <PromptInputTools className="right-chat-btns shrink-0 gap-2">
          {rightActions}
        </PromptInputTools>
      </PromptInputFooter>
    </PromptInput>
  );
}

type ChatComposerActionProps = {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** `send` is the filled primary affordance; there is at most one per bar. */
  tone?: "secondary" | "outline" | "send" | "danger";
  active?: boolean;
  className?: string;
} & Omit<ComponentProps<typeof Button>, "children" | "onClick" | "className">;

const toneVariant = {
  secondary: "secondary",
  outline: "outline",
  send: "default",
  danger: "outline",
} as const;

export function ChatComposerAction({
  label,
  icon,
  onClick,
  disabled,
  tone = "secondary",
  active,
  className,
  ...props
}: ChatComposerActionProps) {
  return (
    <SimpleTooltipButton
      label={label}
      tone={tone}
      active={active}
      disabled={disabled}
      onClick={onClick}
      className={className}
      icon={icon}
      {...props}
    />
  );
}

function SimpleTooltipButton({
  label,
  icon,
  tone = "secondary",
  active,
  className,
  ...props
}: ChatComposerActionProps) {
  return (
    <Button
      type="button"
      variant={toneVariant[tone]}
      size="icon"
      aria-label={label}
      // A `title` rather than a Tooltip: these sit inside a form footer that
      // already portals a menu, and a second portal layer was stealing focus.
      title={label}
      className={cn(
        "rounded-full",
        tone === "danger" &&
          "border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20",
        active && "border-destructive/40 text-destructive",
        className,
      )}
      {...props}
    >
      {icon}
    </Button>
  );
}

/** The filled arrow-up submit button the prototype anchors the bar with. */
export function ChatComposerSend({
  disabled,
  label,
}: {
  disabled?: boolean;
  label?: string;
}) {
  const { t } = useTranslation("chat");
  return (
    <Button
      // `type="submit"` so PromptInput's form handles Enter and click alike.
      type="submit"
      size="icon"
      disabled={disabled}
      aria-label={label ?? t("input.send")}
      title={label ?? t("input.send")}
      className="chat-send-btn rounded-full"
    >
      <ArrowUp className="size-5" />
    </Button>
  );
}

/** A removable pill for an attachment or a context file. */
export function ChatComposerChip({
  label,
  onRemove,
  removeLabel,
  tone = "muted",
}: {
  label: string;
  onRemove: () => void;
  removeLabel?: string;
  tone?: "muted" | "accent";
}) {
  return (
    <span
      className={cn(
        "flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium",
        tone === "muted" && "border-border bg-muted text-foreground",
        // Not `color-mix(in oklch, var(--primary) …)`: the app's tokens are HSL
        // channel triplets, so that computes to nothing. Tailwind's own `/10`
        // modifier wraps the full `hsl(...)` colour and does work.
        tone === "accent" && "border-primary/25 bg-primary/10 text-primary",
      )}
    >
      <span className="max-w-[150px] truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        title={removeLabel}
        aria-label={removeLabel ?? label}
        className="transition-colors hover:text-destructive"
      >
        <IconX size={14} />
      </button>
    </span>
  );
}

export default ChatComposer;
